import { Button, Form, Table } from "antd";
import { FormListProps } from "antd/es/form";
import { ColumnsType } from "antd/es/table";
import { makeAutoObservable } from "mobx";
import { observer } from "mobx-react-lite";
import * as React from "react";
import { AttributesTransferModal } from "src/business/attribute/AttributesTransfer";
import { AttrDetails } from "src/business/attribute/AttributesTransfer/AttrTransferPart/buildAttrsList";
import { AttrVisitor } from "src/business/attribute/AttrVisitor";
import { walkAttrsInSpecs } from "src/business/attribute/AttrVisitor/walkAttr";
import { ZSpecCommon } from "src/business/specification";
import {
  ZWorkflowAttr,
  ZWorkflowStep,
} from "src/business/workflow/ZWorkflowStep";
import { CollapseButton } from "src/components/CollapseButton";

import * as styles from "./SelectWorkflowAttrs.module.less";

type PropsSelectWorkflowAttrs = {
  specs: ZSpecCommon[];
};

const ownerFieldName: keyof ZWorkflowStep = "attributes";

export const SelectWorkflowAttrs: React.FC<PropsSelectWorkflowAttrs> = observer(
  (props) => {
    const { specs } = props;
    const store = React.useMemo(
      () => new SelectWorkflowAttrsStore(specs),
      [specs],
    );
    const rules: FormListProps["rules"] = [
      {
        validator: (_, value: ZWorkflowAttr[] | undefined) =>
          value?.length
            ? Promise.resolve()
            : Promise.reject(Error("Необходимо добавить атрибуты")),
      },
    ];
    const wfAttrs: ZWorkflowAttr[] | undefined = Form.useWatch(ownerFieldName);
    const form = Form.useFormInstance();
    React.useEffect(() => {
      store.setAttrs(wfAttrs ?? []);
    }, [wfAttrs]);
    const onNewAttrs = (list: AttrDetails[]) => {
      form.setFieldValue(ownerFieldName, mergeAttrList(wfAttrs ?? [], list));
    };
    return (
      <div className={styles.frame}>
        <Form.Item
          label="Атрибуты для отображения"
          name={ownerFieldName}
          rules={rules}
          required
          className={styles.formItem}
        />
        <div className={styles.add}>
          <Button onClick={() => store.setOpen(true)}>Выбрать</Button>
        </div>
        <div className={styles.table}>
          <Table
            dataSource={store.visibleRows}
            columns={store.columns}
            pagination={false}
          />
          <AttributesTransferModal
            specs={specs}
            close={() => store.setOpen(false)}
            open={store.open}
            initialAttrIds={wfAttrs?.map(({ attrId }) => attrId) ?? []}
            onApply={onNewAttrs}
          />
        </div>
      </div>
    );
  },
);

type AttrRow = {
  key: string;
  wfAttr: ZWorkflowAttr;
  specName: string;
  attrName: string;
  ownerIds: string[];
  hasNested?: boolean;
  required?: boolean;
};

class SelectWorkflowAttrsStore {
  constructor(public specs: ZSpecCommon[]) {
    makeAutoObservable(this);
  }

  collapsed = new Set<string>();

  toggleCollapse(id: string) {
    const { collapsed } = this;
    if (collapsed.has(id)) {
      collapsed.delete(id);
    } else {
      collapsed.add(id);
    }
  }

  attrs: ZWorkflowAttr[] = [];

  setAttrs(value: ZWorkflowAttr[]) {
    this.attrs = value;
  }

  get allRows(): AttrRow[] {
    return buildAttrRows(this.attrs, this.specs);
  }

  get visibleRows(): AttrRow[] {
    const { collapsed } = this;
    return this.allRows.filter(
      ({ ownerIds }) => !ownerIds.some((id) => collapsed.has(id)),
    );
  }

  open = false;

  setOpen(flag: boolean) {
    this.open = flag;
  }

  get columns(): ColumnsType<AttrRow> {
    return [
      {
        key: "specName",
        title: "Спецификация",
        render: (_, { specName }) => specName,
      },
      {
        key: "attrName",
        title: "Атрибут",
        width: "60%",
        render: (_, row) => <AttrName row={row} store={this} />,
      },
    ];
  }
}

type PropsAttrName = {
  row: AttrRow;
  store: SelectWorkflowAttrsStore;
};
const AttrName: React.FC<PropsAttrName> = observer(({ row, store }) => {
  const { attrName, ownerIds, hasNested, wfAttr } = row;
  const level = ownerIds.length;
  return (
    <span className={styles.attrNameCell}>
      <span style={{ width: 24 * level, display: "inline-block" }} />
      <span className={styles.collapseBox}>
        {hasNested && (
          <CollapseButton
            collapsed={store.collapsed.has(wfAttr.attrId)}
            onChange={() => store.toggleCollapse(wfAttr.attrId)}
          />
        )}
      </span>
      {attrName}
    </span>
  );
});

const buildAttrRows = (
  wfAttrs: ZWorkflowAttr[],
  specs: ZSpecCommon[],
): AttrRow[] => {
  const result: AttrRow[] = [];
  const curOwnerIds: string[] = [];
  const realOwners = new Set<string>();
  const visitor: AttrVisitor = {
    onEach: (attr, { spec }) => {
      if (spec) {
        const wfAttr = wfAttrs.find(
          ({ attrId, specId }) => attrId === attr.uid && specId === spec.id,
        );
        if (wfAttr) {
          result.push({
            key: `${spec.id}:${attr.uid}`,
            wfAttr,
            attrName: attr.name,
            specName: spec.name,
            ownerIds: [...curOwnerIds],
          });
          curOwnerIds.forEach((id) => realOwners.add(id));
        }
      }
    },
    onEnter: ({ uid }) => {
      curOwnerIds.push(uid);
    },
    onLeave: ({ uid }) => {
      curOwnerIds.pop();
      // Проставить признак наличия вложенных элементов
      if (realOwners.has(uid)) {
        const row = result.find(({ wfAttr }) => wfAttr.attrId === uid);
        if (row) row.hasNested = true;
      }
    },
  };
  walkAttrsInSpecs(specs, visitor);
  return result;
};

const mergeAttrList = (
  oldList: ZWorkflowAttr[],
  newList: AttrDetails[],
): ZWorkflowAttr[] => {
  const dict: Record<string, ZWorkflowAttr> = {};
  oldList.forEach((it) => {
    dict[it.attrId] = it;
  });
  return newList.map(({ attr, spec }) => {
    const cached = dict[attr.uid];
    if (cached) return cached;
    return {
      attrId: attr.uid,
      specId: spec.id,
    };
  });
};
